import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";

import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import type { GridColDef } from "@mui/x-data-grid";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDDialog from "components/MDDialog";
import { LojaDataGrid } from "layouts/pages/loja/common";

import FormAdiantamento from "./FormAdiantamento";
import {
  ADIANTAMENTO_ACCENT,
  adiantamentoModalHeaderSx,
  adiantamentoModalPaperSx,
  adiantamentoToolbarSx,
} from "./adiantamento-evento.theme";

import { IAdiantamentoEvento } from "interfaces/adiantamento";
import { IEventoResponse } from "interfaces/eventos";

import { deleteAdiantamentoEvento } from "services/adiantamento";
import { formatCurrency } from "helpers/utils";

interface Props {
  evento: IEventoResponse;
  updateEvento: () => void;
}

type StatusAdiantamento = IAdiantamentoEvento["status"];

function statusChipColor(
  status: StatusAdiantamento,
): "success" | "info" | "warning" | "error" | "default" {
  switch (status) {
    case "Pago":
      return "success";
    case "Agendado":
      return "info";
    case "Solicitado":
      return "warning";
    case "Cancelado":
      return "error";
    default:
      return "default";
  }
}

function formatarData(valor?: string | null): string {
  if (!valor) return "—";
  return format(
    valor.slice(0, 10).concat("T23:59:59.000Z"),
    "dd/MM/yyyy",
  );
}

function StatCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: boolean;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: accent ? "rgba(249, 96, 48, 0.3)" : "grey.200",
        bgcolor: accent ? "rgba(249, 96, 48, 0.04)" : "background.paper",
        height: "100%",
      }}
    >
      <Typography
        variant="h5"
        fontWeight="bold"
        color={accent ? ADIANTAMENTO_ACCENT : "text.primary"}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

export default function AdiantamentoEvento({ evento, updateEvento }: Props) {
  const [formOpen, setFormOpen] = useState(false);
  const [openDialogDelete, setOpenDialogDelete] = useState(false);
  const [adiantamentoSelected, setAdiantamentoSelected] =
    useState<IAdiantamentoEvento | null>(null);

  const listAdiantamentos = evento.adiantamentoEvento ?? [];

  const stats = useMemo(() => {
    const total = listAdiantamentos.length;
    const valorRecebido = listAdiantamentos
      .filter((item) => item.status === "Pago")
      .reduce((sum, item) => sum + Number(item.valorTransacao || 0), 0);
    const pendentes = listAdiantamentos.filter((item) =>
      ["Solicitado", "Agendado"].includes(item.status),
    ).length;
    return { total, valorRecebido, pendentes };
  }, [listAdiantamentos]);

  const handleDeleteAdiantamento = () => {
    if (!adiantamentoSelected?.id) return;
    deleteAdiantamentoEvento(adiantamentoSelected.id).then(() => {
      setOpenDialogDelete(false);
      setAdiantamentoSelected(null);
      updateEvento();
    });
  };

  const abrirNovo = () => {
    setAdiantamentoSelected(null);
    setFormOpen(true);
  };

  const abrirEditar = (adiantamento: IAdiantamentoEvento) => {
    setAdiantamentoSelected(adiantamento);
    setFormOpen(true);
  };

  const fecharForm = () => {
    setFormOpen(false);
    setAdiantamentoSelected(null);
  };

  const columns: GridColDef<IAdiantamentoEvento>[] = [
      {
        field: "solicitante",
        flex: 1,
        minWidth: 160,
        headerName: "Solicitante",
      },
      {
        field: "identificacaoTransacao",
        width: 140,
        headerName: "ID transação",
      },
      {
        field: "dataSolicitacao",
        headerName: "Solicitação",
        width: 110,
        valueFormatter: (params) => formatarData(params.value as string),
      },
      {
        field: "valorTransacao",
        headerName: "Valor",
        width: 120,
        renderCell: (params) => (
          <Typography variant="body2" fontWeight={600}>
            {formatCurrency(Number(params.row.valorTransacao) || 0)}
          </Typography>
        ),
      },
      {
        field: "dataPagamento",
        headerName: "Pagamento",
        width: 110,
        valueFormatter: (params) => formatarData(params.value as string),
      },
      {
        field: "status",
        headerName: "Status",
        width: 120,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.row.status}
            color={statusChipColor(params.row.status)}
            variant={params.row.status === "Pago" ? "filled" : "outlined"}
          />
        ),
      },
      {
        field: "urlComprovante",
        headerName: "",
        width: 52,
        sortable: false,
        renderCell: (params) =>
          params.row.urlComprovante ? (
            <Tooltip title="Baixar comprovante">
              <IconButton
                component={Link}
                to={params.row.urlComprovante}
                target="_blank"
                size="small"
                color="info"
              >
                <DownloadOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Typography variant="caption" color="text.disabled">
              —
            </Typography>
          ),
      },
      {
        field: "acoes",
        headerName: "",
        width: 88,
        sortable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.25}>
            <Tooltip title="Editar">
              <IconButton size="small" color="info" onClick={() => abrirEditar(params.row)}>
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Excluir">
              <IconButton
                size="small"
                color="error"
                onClick={() => {
                  setAdiantamentoSelected(params.row);
                  setOpenDialogDelete(true);
                }}
              >
                <DeleteOutlineOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ];

  return (
    <MDBox>
      <MDDialog
        title="Excluir recebimento"
        message={`Tem certeza que deseja excluir o recebimento de "${adiantamentoSelected?.solicitante}"?`}
        open={openDialogDelete}
        onClose={() => {
          setOpenDialogDelete(false);
          setAdiantamentoSelected(null);
        }}
        response={(resp: boolean) => {
          if (resp) handleDeleteAdiantamento();
          else setOpenDialogDelete(false);
        }}
      />

      <Box sx={adiantamentoToolbarSx}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <PaymentsOutlinedIcon sx={{ color: ADIANTAMENTO_ACCENT }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Recebimentos do evento
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {evento.titulo}
              </Typography>
            </Box>
          </Stack>
          <MDButton
            variant="gradient"
            color="dark"
            onClick={abrirNovo}
            startIcon={<AddOutlinedIcon />}
            sx={{
              flexShrink: 0,
              background: `linear-gradient(195deg, ${ADIANTAMENTO_ACCENT}, #e04a1a)`,
            }}
          >
            Novo recebimento
          </MDButton>
        </Stack>
      </Box>

      <Grid container spacing={2} mb={2.5}>
        <Grid item xs={6} sm={4}>
          <StatCard label="Registros" value={stats.total} accent />
        </Grid>
        <Grid item xs={6} sm={4}>
          <StatCard
            label="Valor recebido (pago)"
            value={formatCurrency(stats.valorRecebido)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard label="Pendentes / agendados" value={stats.pendentes} />
        </Grid>
      </Grid>

      {listAdiantamentos.length === 0 ? (
        <Box
          sx={{
            py: 6,
            px: 2,
            textAlign: "center",
            borderRadius: 2,
            border: "1px dashed",
            borderColor: "grey.300",
            bgcolor: "grey.50",
          }}
        >
          <PaymentsOutlinedIcon sx={{ fontSize: 44, color: "grey.400", mb: 1 }} />
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Nenhum recebimento cadastrado
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Registre adiantamentos e pagamentos recebidos para este evento.
          </Typography>
          <MDButton
            variant="outlined"
            color="dark"
            onClick={abrirNovo}
            startIcon={<AddOutlinedIcon />}
          >
            Adicionar primeiro recebimento
          </MDButton>
        </Box>
      ) : (
        <LojaDataGrid
          rows={listAdiantamentos}
          columns={columns}
          getRowId={(row) => row.id ?? row.identificacaoTransacao}
          autoHeight
          disableColumnMenu
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          pageSizeOptions={[5, 10, 25]}
          localeText={{ noRowsLabel: "Nenhum recebimento" }}
        />
      )}

      <Dialog
        open={formOpen}
        onClose={fecharForm}
        maxWidth={false}
        fullWidth
        PaperProps={{ sx: adiantamentoModalPaperSx }}
      >
        <Box sx={adiantamentoModalHeaderSx}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              component="img"
              src={evento.urlImageDestaque}
              alt={evento.titulo}
              sx={{
                width: 56,
                height: 56,
                borderRadius: 1.5,
                objectFit: "cover",
                border: "2px solid rgba(255,255,255,0.2)",
                flexShrink: 0,
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="overline" sx={{ opacity: 0.75, letterSpacing: 1 }}>
                {adiantamentoSelected ? "Editar recebimento" : "Novo recebimento"}
              </Typography>
              <Typography variant="subtitle1" fontWeight={700} noWrap>
                {evento.titulo}
              </Typography>
            </Box>
            <IconButton onClick={fecharForm} sx={{ color: "inherit" }} aria-label="Fechar">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>

        <DialogContent sx={{ p: { xs: 2, sm: 2.5 }, overflow: "auto" }}>
          <FormAdiantamento
            evento={evento}
            adiantamento={adiantamentoSelected}
            handleClose={fecharForm}
            updateEvento={updateEvento}
          />
        </DialogContent>
      </Dialog>
    </MDBox>
  );
}
